// Composite chat credential threaded through pi-ai's `getApiKey`.
//
// The LLM proxy shim (`llmProxyShim.js`) rewrites every outbound provider
// fetch to `/v1/tenant/llm-proxy` and forwards a single bearer — whatever
// pi-ai's `getApiKey` returned, which the SDK places on `Authorization`
// (OpenAI/OpenRouter/custom) or `x-api-key` (Anthropic). We piggy-back the
// per-request chat id + agent role onto that value so the shim can lift them
// off into `X-Origin-Chat-Id` / `X-Origin-Chat-Role` headers for the proxy's
// audit log, then forward the CLEAN Origin JWT as the bearer.
//
// Why encode into the credential rather than a module global: pi-ai calls
// `getApiKey` synchronously at request time, and sub-agents / concurrent
// chats each carry their own closure. Riding the context on the returned
// value keeps it concurrency-safe by construction — there is no shared
// mutable "current chat" to race.
//
// Format: `origin-chat~v1~<chatId>~<role>~<jwt>`. The `~` separator is not a
// base64url char (JWTs), not a hex/uuid char, and not used by the role
// vocabulary, so the split is unambiguous even though the JWT itself
// contains `.`. A plain (non-composite) credential — the pre-chat-persistence
// behavior — is returned untouched so nothing downstream changes when no chat
// id is in scope.

const PREFIX = "origin-chat~v1~";
// Roles are a tiny closed vocabulary; anything else is coerced to "primary"
// so a caller typo can never inject an arbitrary header value.
const KNOWN_ROLES = new Set(["primary", "task"]);

// Build the credential pi-ai's `getApiKey` returns. With no chat id in scope
// (or a chat id that would break the encoding) the bare jwt is returned, so
// the shim sees exactly today's value and emits no chat headers.
export function encodeChatCredential({ chatId, role, jwt }) {
    const token = typeof jwt === "string" && jwt ? jwt : "no-jwt";
    const id = typeof chatId === "string" ? chatId.trim() : "";
    // A chat id containing the separator would corrupt the split; server chat
    // ids are hyphenated UUIDs (no `~`), but guard rather than trust.
    if (!id || id.includes("~")) return token;
    const safeRole = KNOWN_ROLES.has(role) ? role : "primary";
    return `${PREFIX}${id}~${safeRole}~${token}`;
}

// Inverse of encodeChatCredential. Returns `{ chatId, role, jwt }`; chatId /
// role are null for a plain credential. Never throws — a malformed composite
// degrades to treating the whole value as the bearer.
export function decodeChatCredential(credential) {
    if (typeof credential !== "string" || !credential.startsWith(PREFIX)) {
        return { chatId: null, role: null, jwt: typeof credential === "string" ? credential : "" };
    }
    const rest = credential.slice(PREFIX.length);
    const firstSep = rest.indexOf("~");
    if (firstSep < 0) return { chatId: null, role: null, jwt: rest };
    const secondSep = rest.indexOf("~", firstSep + 1);
    if (secondSep < 0) return { chatId: null, role: null, jwt: rest };
    const chatId = rest.slice(0, firstSep);
    const role = rest.slice(firstSep + 1, secondSep);
    // Everything after the second separator is the JWT verbatim (it may itself
    // contain `.` but never `~`).
    const jwt = rest.slice(secondSep + 1);
    return {
        chatId: chatId || null,
        role: KNOWN_ROLES.has(role) ? role : null,
        jwt,
    };
}

// Header names the shim emits and the proxy audit log reads. Kept here so the
// producer (shim) and any future reader agree on one spelling.
export const CHAT_ID_HEADER = "X-Origin-Chat-Id";
export const CHAT_ROLE_HEADER = "X-Origin-Chat-Role";
